import { status as appointmentStatus } from '@spike/appointment-model';

export type ServiceCategoryType =
    | 'grooming'
    | 'daycare'
    | 'boarding'
    | 'evaluation'
    | 'transportation'
    | 'training'
    | 'walking'
    | 'all';

// Session-based business areas (Walking parameterised by area) share the location and staff filters.
export const sessionServiceCategories: Array<ServiceCategoryType> = ['walking', 'training'];
export const isSessionListType = (listType: string) => (sessionServiceCategories as Array<string>).includes(listType);

export interface TabModel {
    id: string;
    name: string;
    statusIds: Array<number>;
    serviceCategory: ServiceCategoryType;
    backendTabs?: Array<string>;
    mark?: 'error' | 'warning';
    selectedColor?: string;
}

export const groomingIssueTab: TabModel = {
    id: 'grooming_issue',
    name: 'Issues',
    statusIds: [],
    serviceCategory: 'grooming',
    mark: 'error',
    selectedColor: '#E36E74'
};

export const groomingPendingTab: TabModel = {
    id: 'grooming_pending',
    name: 'Pending',
    statusIds: [],
    serviceCategory: 'grooming',
    mark: 'warning'
};

export const groomingUpcomingTab: TabModel = {
    id: 'grooming_upcoming',
    name: 'Upcoming',
    statusIds: [appointmentStatus.BOOKED, appointmentStatus.CONFIRMED],
    serviceCategory: 'grooming'
};

export const groomingHereTab: TabModel = {
    id: 'grooming_here',
    name: 'Here',
    statusIds: [appointmentStatus.CHECKED_IN, appointmentStatus.HERE, appointmentStatus.IN_PROGRESS],
    serviceCategory: 'grooming'
};

export const groomingCompletedTab: TabModel = {
    id: 'grooming_completed',
    name: 'Completed',
    statusIds: [
        appointmentStatus.READY_FOR_CHECK_OUT,
        appointmentStatus.PAYMENT,
        appointmentStatus.WAITING_PAYMENT_CONFIRMATION,
        appointmentStatus.COMPLETED
    ],
    serviceCategory: 'grooming'
};

export const groomingCancelledTab: TabModel = {
    id: 'grooming_cancelled',
    name: 'Canceled',
    statusIds: [appointmentStatus.CANCELLED, appointmentStatus.NO_SHOW, appointmentStatus.DECLINED],
    serviceCategory: 'grooming'
};

export const groomingAllTab: TabModel = {
    id: 'grooming_all',
    name: 'All',
    statusIds: [
        appointmentStatus.BOOKED,
        appointmentStatus.CONFIRMED,
        appointmentStatus.CHECKED_IN,
        appointmentStatus.IN_PROGRESS,
        appointmentStatus.READY_FOR_CHECK_OUT,
        appointmentStatus.PAYMENT,
        appointmentStatus.WAITING_PAYMENT_CONFIRMATION,
        appointmentStatus.COMPLETED,
        appointmentStatus.CANCELLED,
        appointmentStatus.NO_SHOW,
        appointmentStatus.DECLINED
    ],
    serviceCategory: 'grooming'
};

export const daycareIssueTab: TabModel = {
    id: 'daycare_issue',
    name: 'Issues',
    statusIds: [],
    serviceCategory: 'daycare',
    mark: 'error',
    selectedColor: '#E36E74'
};

export const daycarePendingTab: TabModel = {
    id: 'daycare_pending',
    name: 'Pending',
    statusIds: [],
    serviceCategory: 'daycare',
    mark: 'warning'
};

export const daycareUpcomingTab: TabModel = {
    id: 'daycare_upcoming',
    name: 'Upcoming',
    statusIds: [appointmentStatus.BOOKED, appointmentStatus.CONFIRMED],
    serviceCategory: 'daycare'
};

export const daycareHereTab: TabModel = {
    id: 'daycare_here',
    name: 'Checked In',
    statusIds: [appointmentStatus.HERE, appointmentStatus.IN_PROGRESS],
    serviceCategory: 'daycare'
};

export const daycarePickedUpTab: TabModel = {
    id: 'daycare_picked_up',
    name: 'Checked Out',
    statusIds: [appointmentStatus.PICKED_UP, appointmentStatus.PAYMENT],
    serviceCategory: 'daycare'
};

export const daycareCancelledTab: TabModel = {
    id: 'daycare_cancelled',
    name: 'Canceled',
    statusIds: [appointmentStatus.CANCELLED],
    serviceCategory: 'daycare'
};

export const daycareAllTab: TabModel = {
    id: 'daycare_all',
    name: 'All',
    statusIds: [
        appointmentStatus.BOOKED,
        appointmentStatus.CONFIRMED,
        appointmentStatus.HERE,
        appointmentStatus.IN_PROGRESS,
        appointmentStatus.PICKED_UP,
        appointmentStatus.CANCELLED,
        appointmentStatus.COMPLETED,
        appointmentStatus.PAYMENT,
        appointmentStatus.WAITING_PAYMENT_CONFIRMATION
    ],
    serviceCategory: 'daycare'
};

export const boardingIssueTab: TabModel = {
    id: 'boarding_issue',
    name: 'Issues',
    statusIds: [],
    serviceCategory: 'boarding',
    mark: 'error',
    selectedColor: '#E36E74'
};

export const boardingPendingTab: TabModel = {
    id: 'boarding_pending',
    name: 'Pending',
    statusIds: [],
    serviceCategory: 'boarding',
    mark: 'warning'
};

export const boardingArrivalsTab: TabModel = {
    id: 'boarding_arrivals',
    name: 'Arrivals',
    statusIds: [],
    serviceCategory: 'boarding',
    backendTabs: ['arrivals']
};

export const boardingCheckedInTab: TabModel = {
    id: 'boarding_checked_in',
    name: 'Checked In',
    statusIds: [],
    serviceCategory: 'boarding',
    backendTabs: ['checked_in']
};

export const boardingDeparturesTab: TabModel = {
    id: 'boarding_departures',
    name: 'Departures',
    statusIds: [],
    serviceCategory: 'boarding',
    backendTabs: ['departures']
};

export const boardingCheckedOutTab: TabModel = {
    id: 'boarding_checked_out',
    name: 'Checked Out',
    statusIds: [],
    serviceCategory: 'boarding',
    backendTabs: ['checked_out']
};

export const boardingCancelledTab: TabModel = {
    id: 'boarding_cancelled',
    name: 'Canceled',
    statusIds: [],
    serviceCategory: 'boarding',
    backendTabs: ['canceled']
};

export const boardingAllTab: TabModel = {
    id: 'boarding_all',
    name: 'All',
    statusIds: [],
    serviceCategory: 'boarding',
    backendTabs: ['arrivals', 'checked_in', 'checked_out', 'departures', 'canceled', 'all']
};

export const evaluationIssueTab: TabModel = {
    id: 'evaluation_issue',
    name: 'Issues',
    statusIds: [],
    serviceCategory: 'evaluation',
    mark: 'error',
    selectedColor: '#E36E74'
};

export const evaluationPendingTab: TabModel = {
    id: 'evaluation_pending',
    name: 'Pending',
    statusIds: [],
    serviceCategory: 'evaluation',
    mark: 'warning'
};

export const evaluationsUpcomingTab: TabModel = {
    id: 'evaluations_upcoming',
    name: 'Upcoming',
    statusIds: [
        appointmentStatus.BOOKED,
        appointmentStatus.CONFIRMED,
        appointmentStatus.CHECKED_IN,
        appointmentStatus.IN_PROGRESS,
        appointmentStatus.READY_FOR_CHECK_OUT,
        appointmentStatus.PAYMENT,
        appointmentStatus.WAITING_PAYMENT_CONFIRMATION
    ],
    serviceCategory: 'evaluation'
};

export const evaluationsClosedTab: TabModel = {
    id: 'evaluations_closed',
    name: 'Closed',
    statusIds: [appointmentStatus.COMPLETED, appointmentStatus.PAYMENT],
    serviceCategory: 'evaluation'
};

export const evaluationsCancelledTab: TabModel = {
    id: 'evaluations_cancelled',
    name: 'Canceled',
    statusIds: [appointmentStatus.CANCELLED, appointmentStatus.NO_SHOW, appointmentStatus.DECLINED],
    serviceCategory: 'evaluation'
};

export const evaluationsAllTab: TabModel = {
    id: 'evaluations_all',
    name: 'All',
    statusIds: [
        appointmentStatus.BOOKED,
        appointmentStatus.CONFIRMED,
        appointmentStatus.CHECKED_IN,
        appointmentStatus.IN_PROGRESS,
        appointmentStatus.READY_FOR_CHECK_OUT,
        appointmentStatus.PAYMENT,
        appointmentStatus.WAITING_PAYMENT_CONFIRMATION,
        appointmentStatus.COMPLETED,
        appointmentStatus.CANCELLED,
        appointmentStatus.NO_SHOW,
        appointmentStatus.DECLINED
    ],
    serviceCategory: 'evaluation'
};

export const requestedGroomingTab: TabModel = {
    id: 'requested_grooming',
    name: 'Grooming',
    statusIds: [appointmentStatus.REQUESTED],
    serviceCategory: 'grooming'
};

export const requestedDaycareTab: TabModel = {
    id: 'requested_daycare',
    name: 'Daycare',
    statusIds: [appointmentStatus.REQUESTED],
    serviceCategory: 'daycare'
};

export const requestedEvaluationTab: TabModel = {
    id: 'requested_evaluation',
    name: 'Evaluation',
    statusIds: [appointmentStatus.REQUESTED],
    serviceCategory: 'evaluation'
};

export const requestedBoardingTab: TabModel = {
    id: 'requested_boarding',
    name: 'Boarding',
    statusIds: [appointmentStatus.REQUESTED],
    serviceCategory: 'boarding'
};

export const requestedWalkingTab: TabModel = {
    id: 'requested_walking',
    name: 'Walking',
    statusIds: [appointmentStatus.REQUESTED],
    serviceCategory: 'walking'
};

export const requestedTrainingTab: TabModel = {
    id: 'requested_training',
    name: 'Training',
    statusIds: [appointmentStatus.REQUESTED],
    serviceCategory: 'training'
};

export const requestedAllTab: TabModel = {
    id: 'requested_all',
    name: 'All',
    statusIds: [appointmentStatus.REQUESTED],
    serviceCategory: 'all'
};

export const allUpcomingTab: TabModel = {
    id: 'all_upcoming',
    name: 'Upcoming',
    statusIds: [appointmentStatus.BOOKED, appointmentStatus.CONFIRMED],
    serviceCategory: 'all'
};

export const allHereTab: TabModel = {
    id: 'all_here',
    name: 'Here',
    statusIds: [appointmentStatus.CHECKED_IN, appointmentStatus.HERE, appointmentStatus.IN_PROGRESS],
    serviceCategory: 'all'
};

export const allCompletedTab: TabModel = {
    id: 'all_completed',
    name: 'Completed',
    statusIds: [
        appointmentStatus.READY_FOR_CHECK_OUT,
        appointmentStatus.PAYMENT,
        appointmentStatus.WAITING_PAYMENT_CONFIRMATION,
        appointmentStatus.COMPLETED
    ],
    serviceCategory: 'all'
};

export const allCancelledTab: TabModel = {
    id: 'all_cancelled',
    name: 'Canceled',
    statusIds: [appointmentStatus.CANCELLED, appointmentStatus.NO_SHOW, appointmentStatus.DECLINED],
    serviceCategory: 'all'
};

export const allAllTab: TabModel = {
    id: 'all_all',
    name: 'All',
    statusIds: [
        appointmentStatus.BOOKED,
        appointmentStatus.CONFIRMED,
        appointmentStatus.CHECKED_IN,
        appointmentStatus.IN_PROGRESS,
        appointmentStatus.READY_FOR_CHECK_OUT,
        appointmentStatus.PAYMENT,
        appointmentStatus.WAITING_PAYMENT_CONFIRMATION,
        appointmentStatus.COMPLETED,
        appointmentStatus.CANCELLED,
        appointmentStatus.NO_SHOW,
        appointmentStatus.DECLINED
    ],
    serviceCategory: 'all'
};

export const transportationIssueTab: TabModel = {
    id: 'transportation_issue',
    name: 'Issues',
    statusIds: [],
    serviceCategory: 'transportation',
    mark: 'error',
    selectedColor: '#E36E74'
};

export const transportationPendingTab: TabModel = {
    id: 'transportation_pending',
    name: 'Pending',
    statusIds: [],
    serviceCategory: 'transportation',
    mark: 'warning'
};

export const transportationUpcomingTab: TabModel = {
    id: 'transportation_upcoming',
    name: 'Upcoming',
    statusIds: [1 /*Booked*/],
    serviceCategory: 'transportation'
};

export const transportationInProgressTab: TabModel = {
    id: 'transportation_in_progress',
    name: 'In Progress',
    statusIds: [2 /*On My Way*/, 3 /*Arrived*/, 4 /*Picked Up*/],
    serviceCategory: 'transportation'
};

export const transportationCompletedTab: TabModel = {
    id: 'transportation_completed',
    name: 'Completed',
    statusIds: [
        5 /*Completed*/,
        appointmentStatus.PAYMENT,
        appointmentStatus.WAITING_PAYMENT_CONFIRMATION,
        6 /*Close*/
    ],
    serviceCategory: 'transportation'
};

export const transportationCancelledTab: TabModel = {
    id: 'transportation_cancelled',
    name: 'Canceled',
    statusIds: [appointmentStatus.CANCELLED, appointmentStatus.NO_SHOW, appointmentStatus.DECLINED],
    serviceCategory: 'transportation'
};

export const transportationAllTab: TabModel = {
    id: 'transportation_all',
    name: 'All',
    statusIds: [
        1 /*Booked*/,
        2 /*On My Way*/,
        3 /*Arrived*/,
        4 /*Picked Up*/,
        5 /*Completed*/,
        6 /*Close*/,
        appointmentStatus.CANCELLED,
        appointmentStatus.NO_SHOW,
        appointmentStatus.DECLINED
    ],
    serviceCategory: 'transportation'
};

export const trainingIssueTab: TabModel = {
    id: 'training_issue',
    name: 'Issues',
    statusIds: [],
    serviceCategory: 'training',
    mark: 'error',
    selectedColor: '#E36E74'
};

export const trainingPendingTab: TabModel = {
    id: 'training_pending',
    name: 'Pending',
    statusIds: [],
    serviceCategory: 'training',
    mark: 'warning'
};

export const trainingUpcomingTab: TabModel = {
    id: 'training_upcoming',
    name: 'Upcoming',
    statusIds: [appointmentStatus.BOOKED],
    serviceCategory: 'training'
};

export const trainingInProgressTab: TabModel = {
    id: 'training_in_progress',
    name: 'In Progress',
    statusIds: [2 /*ON_MY_WAY */, 3 /*ARRIVED */, 4 /*IN PROGRESS */],
    serviceCategory: 'training'
};

export const trainingCompletedTab: TabModel = {
    id: 'training_completed',
    name: 'Completed',
    statusIds: [
        appointmentStatus.READY_FOR_CHECK_OUT /*COMPLETED*/,
        appointmentStatus.PAYMENT,
        appointmentStatus.WAITING_PAYMENT_CONFIRMATION,
        appointmentStatus.COMPLETED /*CLOSED*/
    ],
    serviceCategory: 'training'
};

export const trainingCancelledTab: TabModel = {
    id: 'training_cancelled',
    name: 'Canceled',
    statusIds: [appointmentStatus.CANCELLED, appointmentStatus.NO_SHOW, appointmentStatus.DECLINED],
    serviceCategory: 'training'
};

export const trainingAllTab: TabModel = {
    id: 'training_all',
    name: 'All',
    statusIds: [
        appointmentStatus.BOOKED,
        2 /*ON_MY_WAY*/,
        3 /*ARRIVED*/,
        4 /*IN PROGRESS*/,
        appointmentStatus.READY_FOR_CHECK_OUT,
        appointmentStatus.PAYMENT,
        appointmentStatus.WAITING_PAYMENT_CONFIRMATION,
        appointmentStatus.COMPLETED,
        appointmentStatus.CANCELLED,
        appointmentStatus.NO_SHOW,
        appointmentStatus.DECLINED
    ],
    serviceCategory: 'training'
};

export const walkingIssueTab: TabModel = {
    id: 'walking_issue',
    name: 'Issues',
    statusIds: [],
    serviceCategory: 'walking',
    mark: 'error',
    selectedColor: '#E36E74'
};

export const walkingPendingTab: TabModel = {
    id: 'walking_pending',
    name: 'Pending',
    statusIds: [],
    serviceCategory: 'walking',
    mark: 'warning'
};

export const walkingUpcomingTab: TabModel = {
    id: 'walking_upcoming',
    name: 'Upcoming',
    statusIds: [appointmentStatus.BOOKED],
    serviceCategory: 'walking'
};

export const walkingInProgressTab: TabModel = {
    id: 'walking_in_progress',
    name: 'In Progress',
    statusIds: [2 /*ON_MY_WAY */, 3 /*ARRIVED */, 4 /*IN PROGRESS */],
    serviceCategory: 'walking'
};

export const walkingCompletedTab: TabModel = {
    id: 'walking_completed',
    name: 'Completed',
    statusIds: [
        appointmentStatus.READY_FOR_CHECK_OUT /*COMPLETED*/,
        appointmentStatus.PAYMENT,
        appointmentStatus.WAITING_PAYMENT_CONFIRMATION,
        appointmentStatus.COMPLETED /*CLOSED*/
    ],
    serviceCategory: 'walking'
};

export const walkingCancelledTab: TabModel = {
    id: 'walking_cancelled',
    name: 'Canceled',
    statusIds: [appointmentStatus.CANCELLED, appointmentStatus.NO_SHOW, appointmentStatus.DECLINED],
    serviceCategory: 'walking'
};

export const walkingAllTab: TabModel = {
    id: 'walking_all',
    name: 'All',
    statusIds: [
        appointmentStatus.BOOKED,
        2 /*ON_MY_WAY*/,
        3 /*ARRIVED*/,
        4 /*IN PROGRESS*/,
        appointmentStatus.READY_FOR_CHECK_OUT,
        appointmentStatus.PAYMENT,
        appointmentStatus.WAITING_PAYMENT_CONFIRMATION,
        appointmentStatus.COMPLETED,
        appointmentStatus.CANCELLED,
        appointmentStatus.NO_SHOW,
        appointmentStatus.DECLINED
    ],
    serviceCategory: 'walking'
};

export const groomingTabs = [
    groomingIssueTab,
    groomingPendingTab,
    groomingUpcomingTab,
    groomingHereTab,
    groomingCompletedTab,
    groomingCancelledTab,
    groomingAllTab
];

export const daycareTabs = [
    daycareIssueTab,
    daycarePendingTab,
    daycareUpcomingTab,
    daycareHereTab,
    daycarePickedUpTab,
    daycareCancelledTab,
    daycareAllTab
];

export const boardingTabs = [
    boardingIssueTab,
    boardingPendingTab,
    boardingArrivalsTab,
    boardingCheckedInTab,
    boardingDeparturesTab,
    boardingCheckedOutTab,
    boardingCancelledTab,
    boardingAllTab
];
export const evaluationTabs = [
    evaluationIssueTab,
    evaluationPendingTab,
    evaluationsUpcomingTab,
    evaluationsClosedTab,
    evaluationsCancelledTab,
    evaluationsAllTab
];

export const requestedTabs = [
    requestedGroomingTab,
    requestedDaycareTab,
    requestedBoardingTab,
    requestedEvaluationTab,
    requestedWalkingTab,
    requestedTrainingTab,
    requestedAllTab
];

export const transportationTabs = [
    transportationIssueTab,
    transportationPendingTab,
    transportationUpcomingTab,
    transportationInProgressTab,
    transportationCompletedTab,
    transportationCancelledTab,
    transportationAllTab
];

export const trainingTabs = [
    trainingIssueTab,
    trainingPendingTab,
    trainingUpcomingTab,
    trainingInProgressTab,
    trainingCompletedTab,
    trainingCancelledTab,
    trainingAllTab
];

export const walkingTabs = [
    walkingIssueTab,
    walkingPendingTab,
    walkingUpcomingTab,
    walkingInProgressTab,
    walkingCompletedTab,
    walkingCancelledTab,
    walkingAllTab
];

export const allTabs = [allUpcomingTab, allHereTab, allCompletedTab, allCancelledTab, allAllTab];

const tabs = [
    ...groomingTabs,
    ...daycareTabs,
    ...boardingTabs,
    ...evaluationTabs,
    ...requestedTabs,
    ...transportationTabs,
    ...trainingTabs,
    ...walkingTabs,
    ...allTabs
];

export const getStatusIdsByTabId = (tabId: string | null) => tabs.find(tab => tab.id === tabId)?.statusIds ?? [];
export const getServiceCategoryByTabId = (tabId: string | null) =>
    tabs.find(tab => tab.id === tabId)?.serviceCategory ?? null;
export const getBackendTabsByTabId = (tabId: string | null) => tabs.find(tab => tab.id === tabId)?.backendTabs ?? [];

const tabsByServiceCategory: Record<ServiceCategoryType, Array<TabModel>> = {
    grooming: groomingTabs,
    daycare: daycareTabs,
    boarding: boardingTabs,
    evaluation: evaluationTabs,
    transportation: transportationTabs,
    training: trainingTabs,
    walking: walkingTabs,
    all: allTabs
};

const requestedTabByServiceCategory: Partial<Record<ServiceCategoryType, TabModel>> = {
    grooming: requestedGroomingTab,
    daycare: requestedDaycareTab,
    boarding: requestedBoardingTab,
    evaluation: requestedEvaluationTab,
    walking: requestedWalkingTab,
    training: requestedTrainingTab,
    all: requestedAllTab
};

const defaultTabByServiceCategory: Record<ServiceCategoryType, TabModel> = {
    grooming: groomingUpcomingTab,
    daycare: daycareUpcomingTab,
    boarding: boardingArrivalsTab,
    evaluation: evaluationsUpcomingTab,
    transportation: transportationUpcomingTab,
    training: trainingUpcomingTab,
    walking: walkingUpcomingTab,
    all: allUpcomingTab
};

export const getServiceCategoryByBusinessArea = (businessArea: string | null | undefined): ServiceCategoryType => {
    switch (businessArea) {
        case 'grooming':
        case 'dental':
            return 'grooming';
        case 'daycare':
            return 'daycare';
        case 'boarding':
            return 'boarding';
        case 'evaluation':
            return 'evaluation';
        case 'transportation':
            return 'transportation';
        case 'training':
            return 'training';
        case 'walking':
            return 'walking';
        default:
            return 'all';
    }
};

export interface BookingDestination {
    listType: string;
    tabId: string;
}

/**
 * Resolves which Bookings module (listType) and tab a booking should open in,
 * based on its status and service business area. Used to open the right tab
 * when navigating to a booking from a notification (SU-2165).
 */
export const getBookingDestination = (
    statusId: number | null | undefined,
    businessArea: string | null | undefined
): BookingDestination => {
    const serviceCategory = getServiceCategoryByBusinessArea(businessArea);

    if (statusId === appointmentStatus.REQUESTED) {
        const requestedTab = requestedTabByServiceCategory[serviceCategory] ?? requestedAllTab;
        return { listType: 'requested', tabId: requestedTab.id };
    }

    const categoryTabs = tabsByServiceCategory[serviceCategory] ?? allTabs;
    const matchingTabs =
        typeof statusId === 'number' ? categoryTabs.filter(tab => tab.statusIds.includes(statusId)) : [];

    // Prefer a specific status tab over the catch-all "All" tab.
    const tab =
        matchingTabs.find(t => !t.id.endsWith('_all')) ??
        matchingTabs[0] ??
        defaultTabByServiceCategory[serviceCategory];

    return { listType: serviceCategory, tabId: tab.id };
};
